import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import { useDealerContext } from '@dh-io-emh/shared/src/composables/dealer-context.composable';
import { useUserContext } from '@dh-io-emh/shared/src/composables/user-context.composable';
import {
  FilterPayload,
  getDefaultSortOrder,
  getState,
  productsDispatchers,
  ProductsState,
} from '@dh-io-emh/shared/src/stores/products/products.connection';
import { parseQueryParams, updateQueryParams } from '@dh-io-emh/shared/src/utils/filter-query-params.util';
import { RouteQuery } from '@dh-io-emh/shared/src/router/types';
import { FilterCode } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';

const changeFilterValue = (filter: FilterPayload) => {
  productsDispatchers.setFilterValue({ filterCode: filter.filterCode, filterValue: filter.filterValue });

  if (filter.filterCode === FilterCode.locations && !filter.filterValue.length) {
    productsDispatchers.setDealerFilterInfo(undefined);
  }
};

/**
 * Possible SRP initialization scenarios:
 * 1. User visits SRP first time, no cached results in session storage:
 *    1a. Check if there are sort & assortment params in the URL, if not use default values
 *    1b. Make initial call to search API so we know which filters exist
 *    1c. Based on the initial filters check if query params contain any of those filters.
 *          If yes: update filter values in store and do another search API call
 *          If no : we are done
 * 2. User comes back to SRP during current browser session, e.g. by going to PDP and back, or via deep link,
 *    so we might have some cached search data available. In that case we need to do some validation if we should
 *    display the cached data or not:
 *    2a. Check if any of these parameters have changed compared to the cached data, since they affect the search results:
 *        Query params: sort, vehicleAssortment, filters
 *        Others: dealer context
 *    2b. Has anything changed?
 *        If yes: update parameters in store and call search API to get fresh results
 *        If no : we are done, keep cached data
 * Finally: update query params to reflect selected filters and potentially missing sort & assortment params
 */
export const loadSrpData = async () => {
  const {
    srp: { availableCategories },
  } = infuse('config');
  const router = infuse('router');
  const { query } = router.getCurrentRoute();
  const { dealerContext } = useDealerContext();
  const userContext = useUserContext();

  let state = await getState();
  const { sort, assortment } = getSortAndAssortmentParams(query, availableCategories, state);
  const hasCachedData = state.results.length > 0 || state.filters.length > 0;

  if (hasCachedData) {
    // We're dealing with cached data, check if query params are different from current state
    const hasCategoryChanged = assortment !== state.selectedCategory;
    const hasSortChanged = sort !== state.selectedSort;
    const hasDealerContextChanged = state.dealerContext?.companyId !== dealerContext.value?.companyId;
    const hasUserContextChanged = state.userContext !== userContext.value;
    const { hasChangedFilters, changedFilters } = parseQueryParams({
      filters: state.filters,
      query,
    });

    const hasAnythingChanged =
      hasCategoryChanged || hasSortChanged || hasDealerContextChanged || hasChangedFilters || hasUserContextChanged;

    if (!hasAnythingChanged) {
      // Nothing changed, keep cached results and refresh dealer info if necessary
      productsDispatchers.loadDealerInfo(state.results);
    } else {
      // Something changed, either filters, sort param, assortment param or dealer context, update values in store
      productsDispatchers.setCategory(assortment as string);
      productsDispatchers.setSortOrder(sort as string);
      productsDispatchers.setDealerContext(dealerContext.value);
      productsDispatchers.setUserContext(userContext.value);
      productsDispatchers.resetPaging();
      // Apply filters extracted from query
      changedFilters.forEach(changeFilterValue);
      // Fetch products based on new filters
      await productsDispatchers.getSearchProducts();
    }
  } else {
    // If there are no cached results:
    // First we have to make an initial request so we know which filters exists.
    // Once we have the initial filter values we can check the query params for additional filters and trigger another request
    // with those filters set.
    productsDispatchers.setCategory(assortment as string);
    productsDispatchers.setSortOrder(sort as string);
    productsDispatchers.setDealerContext(dealerContext.value);
    productsDispatchers.setUserContext(userContext.value);

    state = await getState();

    // Call the search API to get initial results & filters
    await productsDispatchers.getSearchProducts();

    state = await getState();

    // Check if there are any filters in query params with different values
    const { hasChangedFilters, changedFilters } = parseQueryParams({
      filters: state.filters,
      query,
    });

    /**
     * We need to reload filters if the dealer context is activated and locations filters is present
     * to only show the dealer cars (for that we need to filter additionally by the dealer locations)
     */
    const shouldFilterByDealerContextLocations =
      dealerContext.value && state.filters.find(({ code }) => code === FilterCode.locations);

    if (hasChangedFilters || shouldFilterByDealerContextLocations) {
      // Apply filters extracted from query
      changedFilters.forEach(changeFilterValue);

      // Fetch products based on new filters
      await productsDispatchers.getSearchProducts();
    }
  }

  state = await getState();

  // Finally update query params to reflect current state
  updateQueryParams(router, { sort: state.selectedSort, assortment: state.selectedCategory, filters: state.filters });
};

/**
 * Helper function to extract optional sort & assortment params from query.
 * If not present in query then values from current state will be used,
 * if not present in state then default values will be used.
 */
const getSortAndAssortmentParams = (
  query: RouteQuery,
  availableCategories: { name: string }[],
  state: ProductsState,
) => {
  const sort = query.sort || state.selectedSort || getDefaultSortOrder();

  const defaultCategory = availableCategories[0].name;
  let assortment = query.vehicleAssortment || state.selectedCategory || defaultCategory;

  // Ensure assortment is valid
  const isAssortmentValid = availableCategories.some((category) => category.name === assortment);

  // If not valid use default assortment
  if (!isAssortmentValid) {
    assortment = defaultCategory;
  }

  return { sort, assortment };
};
